import {
  CalendarOutlined,
  CheckOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Card, message, Popconfirm, Popover, Segmented, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { staffApi } from "api/staff.api";
import { workScheduleApi } from "api/workSchedule.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ActionButton, TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { useClientPaging } from "hooks/useClientPaging";
import { useSearchInput } from "hooks/useSearchInput";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userStore } from "store/userStore";
import { Staff } from "types/staff";
import { WorkSchedule } from "types/workSchedule";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";
import { WorkScheduleModal } from "./components/WorkScheduleModal";
import "./WorkSchedule.scss";

const schedulePath = "/work-schedule";
const VIEW_KEY = "workScheduleView";
const MAX_CHIPS = 3;
const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

type ViewMode = "calendar" | "list";
type StaffItem = { id: number; name: string };

// Mỗi nhân viên một màu cố định theo thứ tự trong danh sách nhân viên
const STAFF_COLORS = [
  { bg: "#e6f4ff", border: "#1677ff", text: "#0958d9" },
  { bg: "#f6ffed", border: "#52c41a", text: "#389e0d" },
  { bg: "#fff7e6", border: "#fa8c16", text: "#d46b08" },
  { bg: "#f9f0ff", border: "#722ed1", text: "#531dab" },
  { bg: "#fff0f6", border: "#eb2f96", text: "#c41d7f" },
  { bg: "#e6fffb", border: "#13c2c2", text: "#08979c" },
  { bg: "#fffbe6", border: "#faad14", text: "#d48806" },
  { bg: "#fff1f0", border: "#f5222d", text: "#cf1322" },
];
const NO_STAFF_COLOR = { bg: "#f2f4f7", border: "#98a2b3", text: "#475467" };

const shortName = (name: string) => name.trim().split(/\s+/).pop() || name;

const readView = (): ViewMode => {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "calendar";
  } catch {
    return "calendar";
  }
};

const timeRange = (schedule: WorkSchedule) => {
  const start = dayjs.unix(schedule.startAt);
  const end = dayjs.unix(schedule.endAt);
  return start.isSame(end, "day")
    ? `${start.format("DD/MM/YYYY HH:mm")} - ${end.format("HH:mm")}`
    : `${start.format("DD/MM/YYYY HH:mm")} - ${end.format("DD/MM/YYYY HH:mm")}`;
};

export const WorkSchedulePage = observer(({ title = "" }) => {
  const [view, setView] = useState<ViewMode>(readView);
  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [schedules, setSchedules] = useState<WorkSchedule[]>([]);
  const [staffs, setStaffs] = useState<StaffItem[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const searchInput = useSearchInput();
  const modalRef = useRef<WorkScheduleModal>(null);

  const onlyMine = !userStore.info.role?.isAdmin && can(schedulePath, "ONLY_MINE");
  const canCreate = can(schedulePath, "CREATE");
  const canUpdate = can(schedulePath, "UPDATE");
  const canDelete = can(schedulePath, "DELETE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const changeView = useCallback((next: ViewMode) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // không lưu được thì bỏ qua
    }
  }, []);

  // Danh sách nhân viên (ít người) để hiện chip bật/tắt
  useEffect(() => {
    if (onlyMine) return;
    staffApi.findAll({ page: 1, limit: 100 }).then((res) => {
      const list: Staff[] = res.data.staffs || [];
      setStaffs(
        list
          .filter((staff) => !staff.isBlocked)
          .sort((a, b) => a.id - b.id)
          .map((staff) => ({ id: staff.id, name: staff.name || staff.username })),
      );
    });
  }, [onlyMine]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workScheduleApi.findAll({ month: monthValue.month() + 1, year: monthValue.year() });
      setSchedules(res.data.schedules || []);
    } finally {
      setLoading(false);
    }
  }, [monthValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Người có lịch nhưng không nằm trong danh sách (đã khóa...) vẫn hiện chip, xếp sau cùng
  const staffItems = useMemo<StaffItem[]>(() => {
    const known = new Set(staffs.map((staff) => staff.id));
    const extras = new Map<number, StaffItem>();
    schedules.forEach((schedule) => {
      const staff = schedule.staff;
      if (staff && !known.has(staff.id) && !extras.has(staff.id)) {
        extras.set(staff.id, { id: staff.id, name: staff.name || staff.username });
      }
    });
    return [...staffs, ...Array.from(extras.values())];
  }, [staffs, schedules]);

  const colorOf = useCallback(
    (staffId?: number) => {
      const index = staffItems.findIndex((staff) => staff.id === staffId);
      return index < 0 ? NO_STAFF_COLOR : STAFF_COLORS[index % STAFF_COLORS.length];
    },
    [staffItems],
  );

  const visibleSchedules = useMemo(
    () => (onlyMine ? schedules : schedules.filter((schedule) => !schedule.staff || !hiddenIds.has(schedule.staff.id))),
    [schedules, hiddenIds, onlyMine],
  );

  const toggleStaff = useCallback((staffId: number) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }, []);

  const days = useMemo(() => {
    const first = monthValue.startOf("month").startOf("isoWeek");
    const last = monthValue.endOf("month").endOf("isoWeek");
    const list: Dayjs[] = [];
    for (let day = first; !day.isAfter(last, "day"); day = day.add(1, "day")) list.push(day);
    return list;
  }, [monthValue]);

  // Lịch nhiều ngày hiện ở mọi ngày nó đi qua
  const schedulesByDay = useMemo(() => {
    const map = new Map<string, WorkSchedule[]>();
    visibleSchedules.forEach((schedule) => {
      const end = dayjs.unix(schedule.endAt).startOf("day");
      for (let day = dayjs.unix(schedule.startAt).startOf("day"); !day.isAfter(end, "day"); day = day.add(1, "day")) {
        const key = day.format("YYYY-MM-DD");
        map.set(key, [...(map.get(key) || []), schedule]);
      }
    });
    return map;
  }, [visibleSchedules]);

  const openEdit = useCallback((schedule: WorkSchedule) => modalRef.current?.handleUpdate(schedule), []);
  const openCreate = useCallback((date: Dayjs) => modalRef.current?.handleCreate(date), []);

  const handleDelete = useCallback(
    async (schedule: WorkSchedule) => {
      await workScheduleApi.delete(schedule.id);
      message.success("Đã xóa lịch công tác");
      fetchData();
    },
    [fetchData],
  );

  const renderChip = (schedule: WorkSchedule, day: Dayjs) => {
    const color = colorOf(schedule.staff?.id);
    const startsToday = dayjs.unix(schedule.startAt).isSame(day, "day");
    const staffName = schedule.staff ? schedule.staff.name || schedule.staff.username : "";
    return (
      <Tooltip
        key={schedule.id}
        title={
          <div>
            <div className="font-semibold">{schedule.title}</div>
            <div>{timeRange(schedule)}</div>
            {staffName && <div>Nhân viên: {staffName}</div>}
            {schedule.customer && <div>Khách hàng: {schedule.customer.name}</div>}
            {schedule.address && <div>Địa chỉ: {schedule.address}</div>}
          </div>
        }
      >
        <span
          className="workSchedule__chip"
          style={{ background: color.bg, borderLeftColor: color.border, color: color.text }}
          onClick={(event) => {
            event.stopPropagation();
            openEdit(schedule);
          }}
        >
          {!startsToday && "↳ "}
          {schedule.result && <CheckOutlined className="mr-1" />}
          {!onlyMine && staffName && <b>{shortName(staffName)} · </b>}
          {schedule.title}
        </span>
      </Tooltip>
    );
  };

  // ======================= DANH SÁCH DẠNG BẢNG =======================
  const listRows = useMemo(() => {
    const keyword = searchInput.debounced.trim().toLowerCase();
    if (!keyword) return visibleSchedules;
    return visibleSchedules.filter((schedule) =>
      [schedule.title, schedule.staff?.name, schedule.staff?.username, schedule.customer?.name, schedule.address]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [visibleSchedules, searchInput.debounced]);

  const paging = useClientPaging(listRows);

  const columns = useMemo<TableColumnsType<WorkSchedule>>(
    () => [
      ...(onlyMine
        ? []
        : [
            {
              title: "Nhân viên",
              key: "staff",
              width: 170,
              render: (_: unknown, row: WorkSchedule) =>
                row.staff ? (
                  <span className="workSchedule__legend">
                    <span className="workSchedule__legendDot" style={{ background: colorOf(row.staff.id).border }} />
                    {row.staff.name || row.staff.username}
                  </span>
                ) : (
                  "--"
                ),
            },
          ]),
      {
        title: "Tiêu đề",
        dataIndex: "title",
        key: "title",
        render: (value: string, row) => (
          <span className="font-semibold">
            {row.result && <CheckOutlined className="mr-1 text-green-600" />}
            {value}
          </span>
        ),
      },
      {
        title: "Thời gian",
        key: "time",
        width: 230,
        render: (_, row) => <TableContent>{timeRange(row)}</TableContent>,
      },
      { title: "Khách hàng", key: "customer", render: (_, row) => <TableContent>{row.customer?.name || "--"}</TableContent> },
      { title: "Địa chỉ", dataIndex: "address", key: "address", render: (value: string) => <TableContent>{value || "--"}</TableContent> },
      {
        title: "Mục tiêu",
        dataIndex: "goal",
        key: "goal",
        render: (value: string) => <div className="workSchedule__clamp">{value || "--"}</div>,
      },
      {
        title: "Kết quả",
        dataIndex: "result",
        key: "result",
        render: (value: string) => <div className="workSchedule__clamp">{value || "--"}</div>,
      },
      {
        title: "Thao tác",
        key: "actions",
        width: 110,
        align: "center" as const,
        render: (_, row) => (
          <div className="flex gap-2 justify-center items-center" onClick={(event) => event.stopPropagation()}>
            <Tooltip title={canUpdate ? "Cập nhật" : "Xem"}>
              <ActionButton aria-label={canUpdate ? "Cập nhật" : "Xem"} onClick={() => openEdit(row)}>
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Xóa">
                <Popconfirm cancelText="Không" okText="Xóa" title="Xác nhận xóa?" onConfirm={() => handleDelete(row)}>
                  <ActionButton aria-label="Xóa">
                    <DeleteOutlined style={{ color: "#ef4444" }} />
                  </ActionButton>
                </Popconfirm>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [onlyMine, colorOf, canUpdate, canDelete, openEdit, handleDelete],
  );

  const today = dayjs();

  const monthNav = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1">
        <AppButton tone="light" aria-label="Tháng trước" onClick={() => setMonthValue((prev) => prev.subtract(1, "month"))}>
          <LeftOutlined />
        </AppButton>
        <div className="min-w-[90px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
        <AppButton tone="light" aria-label="Tháng sau" onClick={() => setMonthValue((prev) => prev.add(1, "month"))}>
          <RightOutlined />
        </AppButton>
      </div>
      <AppButton tone="light" onClick={() => setMonthValue(dayjs().startOf("month"))}>
        Hôm nay
      </AppButton>
      <Segmented
        value={view}
        onChange={(value) => changeView(value as ViewMode)}
        options={[
          { value: "calendar", label: "Lịch", icon: <CalendarOutlined /> },
          { value: "list", label: "Danh sách", icon: <UnorderedListOutlined /> },
        ]}
      />
    </div>
  );

  const addButton = canCreate && (
    <AppButton tone="primary" icon={<PlusOutlined />} onClick={() => openCreate(dayjs())}>
      Thêm lịch công tác
    </AppButton>
  );

  const staffToggles = !onlyMine && staffItems.length > 0 && (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-xs text-gray-500 font-semibold uppercase mr-1">Nhân viên</span>
      {staffItems.map((staff) => {
        const off = hiddenIds.has(staff.id);
        const color = colorOf(staff.id);
        return (
          <span
            key={staff.id}
            role="button"
            className={`workSchedule__staff ${off ? "workSchedule__staff--off" : ""}`}
            style={off ? undefined : { background: color.bg, borderColor: color.border, color: color.text }}
            onClick={() => toggleStaff(staff.id)}
          >
            <span className="workSchedule__legendDot" style={{ background: off ? "#d0d5dd" : color.border }} />
            {staff.name}
          </span>
        );
      })}
      <a className="text-xs ml-2 workSchedule__link" onClick={() => setHiddenIds(new Set())}>
        Bật tất cả
      </a>
      <a className="text-xs workSchedule__link" onClick={() => setHiddenIds(new Set(staffItems.map((staff) => staff.id)))}>
        Tắt tất cả
      </a>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 workSchedule">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          {monthNav}
          {addButton}
        </div>

        {staffToggles}

        {view === "calendar" && (
          <div className="workSchedule__scroll">
            <div className="workSchedule__grid">
              {WEEKDAYS.map((weekday) => (
                <div key={weekday} className="workSchedule__weekday">
                  {weekday}
                </div>
              ))}
              {days.map((day) => {
                const items = schedulesByDay.get(day.format("YYYY-MM-DD")) || [];
                const outside = !day.isSame(monthValue, "month");
                const extra = items.length - MAX_CHIPS;
                return (
                  <div
                    key={day.format("YYYY-MM-DD")}
                    className={[
                      "workSchedule__cell",
                      outside ? "workSchedule__cell--outside" : "",
                      canCreate ? "workSchedule__cell--clickable" : "",
                    ].join(" ")}
                    onClick={() => canCreate && openCreate(day)}
                  >
                    <span
                      className={`workSchedule__dayNumber ${day.isSame(today, "day") ? "workSchedule__dayNumber--today" : ""}`}
                    >
                      {day.date()}
                    </span>
                    {items.slice(0, MAX_CHIPS).map((schedule) => renderChip(schedule, day))}
                    {extra > 0 && (
                      <Popover
                        trigger="click"
                        title={`Ngày ${day.format("DD/MM/YYYY")} (${items.length} lịch)`}
                        content={<div style={{ width: 300 }}>{items.map((schedule) => renderChip(schedule, day))}</div>}
                      >
                        <div className="workSchedule__more" onClick={(event) => event.stopPropagation()}>
                          +{extra} lịch khác
                        </div>
                      </Popover>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {view === "list" && (
        <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: searchInput.value,
                width: 360,
                placeholder: "Tìm theo tiêu đề, nhân viên, khách hàng, địa chỉ",
                onChange: searchInput.onChange,
                inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
              }}
              filterLabel={null}
            />
          }
          table={
            <AppTable<WorkSchedule>
              rowKey="id"
              loading={loading}
              pagination={false}
              dataSource={paging.pageRows}
              columns={columns}
              onRow={(row) => ({ onClick: () => openEdit(row), style: { cursor: "pointer" } })}
            />
          }
          footer={
            <TableLayoutFooter
              showingFrom={paging.showingFrom}
              showingTo={paging.showingTo}
              total={paging.total}
              currentPage={paging.page}
              pageSize={paging.limit}
              onChange={paging.onChange}
            />
          }
        />
      )}

      <WorkScheduleModal ref={modalRef} onSubmitOk={fetchData} />
    </div>
  );
});
